/**
 * StudentVoiceTab — surfaces the student survey system on both the
 * university page (uni-wide) and, when embedded on a degree page,
 * the course-specific version.
 *
 * Two panels stacked:
 *   1. Aggregate results (only when ≥5 approved responses exist).
 *   2. StudentSurveyForm — always visible so any student can respond.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StudentSurveyForm from "@/components/app/StudentSurveyForm";
import { MessageSquareQuote, Star, TrendingUp } from "lucide-react";

export default function StudentVoiceTab({
  universityId,
  degreeId = null,
  courseTitle = null,
}) {
  const [agg, setAgg] = useState(null);

  const load = () => {
    const url = degreeId
      ? `/surveys/aggregate?university_id=${universityId}&degree_id=${degreeId}`
      : `/surveys/aggregate?university_id=${universityId}`;
    api.get(url).then((r) => setAgg(r.data)).catch(() => setAgg(null));
  };

  useEffect(() => { load(); }, [universityId, degreeId]);

  return (
    <div className="space-y-10" data-testid={degreeId ? "course-survey-panel" : "uni-survey-panel"}>
      <SurveyAggregate data={agg} isCourse={!!degreeId} />

      <div className="rounded-lg border border-stone-200 bg-white p-5 sm:p-7">
        <div className="mb-5">
          <div className="label-uppercase text-[#4C1D95]">
            {degreeId ? "Rate this course" : "Rate this university"}
          </div>
          <h3 className="mt-1 font-editorial text-2xl text-[#002147]">
            {degreeId
              ? "Studied this course? Share your honest take."
              : "Studied here? Share your honest take."}
          </h3>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed max-w-2xl">
            First-party, moderated responses feed our Satisfaction rank
            and the honest per-uni breakdown below. Nothing you write
            is attributed to your name publicly.
          </p>
        </div>

        <StudentSurveyForm
          universityId={universityId}
          degreeId={degreeId}
          courseTitle={courseTitle}
          onSuccess={load}
        />
      </div>
    </div>
  );
}


function SurveyAggregate({ data, isCourse }) {
  if (!data) return null;
  const belowThreshold = data.n_approved < data.min_required;

  if (belowThreshold) {
    return (
      <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50/60 p-5 text-sm text-stone-700" data-testid="survey-below-threshold">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-semibold text-stone-800">Collecting responses…</span>
          <span className="font-mono text-[11px] text-stone-500">
            {data.n_approved} of {data.min_required} needed
          </span>
        </div>
        <p className="mt-2 leading-relaxed">
          We publish aggregate ratings and student quotes on this page
          once at least {data.min_required} moderated responses exist.
          Every response counts — use the form below.
        </p>
      </div>
    );
  }

  const ratings = data.ratings || {};
  // Sort by normalized-to-100 score so the 0-10 NPS field doesn't
  // always appear "highest" against the other 1-5 ratings.
  const sortedRatings = Object.entries(ratings).sort((a, b) => {
    const na = (b[1].avg || 0) / (b[1].scale || 5);
    const nb = (a[1].avg || 0) / (a[1].scale || 5);
    return na - nb;
  });
  const positive = (data.quotes || []).filter((q) => q.kind === "positive");
  const negative = (data.quotes || []).filter((q) => q.kind === "negative");

  return (
    <div className="rounded-lg border border-stone-200 bg-white overflow-hidden" data-testid="survey-aggregate">
      <div className="bg-[#002147] text-white px-5 py-4 flex flex-wrap items-baseline justify-between gap-3">
        <div className="font-mono text-sm uppercase tracking-wider flex items-center gap-2">
          <MessageSquareQuote className="h-4 w-4" />
          {isCourse ? "Course · Student Voice" : "University · Student Voice"}
        </div>
        <div className="text-[11px] text-stone-300 font-mono">
          {data.n_approved} approved response{data.n_approved === 1 ? "" : "s"}
          {data.nps && (
            <span
              className={"ml-2 " + (data.nps.score >= 30 ? "text-emerald-300" : data.nps.score < 0 ? "text-red-300" : "text-amber-200")}
              data-testid="survey-nps-headline"
            >
              · NPS {data.nps.score >= 0 ? "+" : ""}{data.nps.score}
            </span>
          )}
          {data.would_recommend_pct != null && (
            <span className="ml-2 text-emerald-300">
              · {data.would_recommend_pct}% would recommend
            </span>
          )}
        </div>
      </div>

      {data.nps && (
        <div className="px-5 pt-5" data-testid="survey-nps-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-1">
            <NpsSegment label="Promoters"  pct={data.nps.promoters_pct}  tint="emerald" />
            <NpsSegment label="Passives"   pct={data.nps.passives_pct}   tint="amber" />
            <NpsSegment label="Detractors" pct={data.nps.detractors_pct} tint="red" />
          </div>
          <p className="text-[11px] font-mono text-stone-500 uppercase tracking-wider">
            Net Promoter Score based on {data.nps.n} responses to "how likely are you to recommend?"
          </p>
        </div>
      )}

      <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ratings column */}
        <div className="lg:col-span-1">
          <div className="label-uppercase mb-3">Average ratings</div>
          <div className="space-y-3">
            {sortedRatings.map(([k, v]) => (
              <RatingBar key={k} label={FIELD_LABEL[k] || k} avg={v.avg} n={v.n} scale={v.scale || 5} />
            ))}
          </div>
        </div>
        {/* Quotes columns */}
        <div>
          <div className="label-uppercase mb-3 text-emerald-800">Loved</div>
          <div className="space-y-3">
            {positive.length === 0 && (
              <p className="text-[13px] text-stone-500">No positive quotes yet.</p>
            )}
            {positive.slice(0, 3).map((q, i) => (
              <QuoteCard key={i} quote={q} tint="emerald" />
            ))}
          </div>
        </div>
        <div>
          <div className="label-uppercase mb-3 text-red-800">Complained about</div>
          <div className="space-y-3">
            {negative.length === 0 && (
              <p className="text-[13px] text-stone-500">No critical quotes yet.</p>
            )}
            {negative.slice(0, 3).map((q, i) => (
              <QuoteCard key={i} quote={q} tint="red" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RatingBar({ label, avg, n, scale = 5 }) {
  const pct = Math.round((avg / scale) * 100);
  return (
    <div>
      <div className="flex justify-between items-baseline text-[13px]">
        <span className="text-stone-700">{label}</span>
        <span>
          <span className="font-mono font-semibold text-[#002147]">{avg}</span>
          <span className="font-mono text-stone-400 text-[10px] ml-1">/{scale} · n={n}</span>
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-stone-100 overflow-hidden">
        <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function NpsSegment({ label, pct, tint }) {
  const bg = tint === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
           : tint === "amber"   ? "bg-amber-50 border-amber-200 text-amber-900"
                                : "bg-red-50 border-red-200 text-red-900";
  return (
    <div className={`rounded border ${bg} px-3 py-2 flex items-baseline justify-between`}>
      <span className="text-[11px] font-underground-sc tracking-wider">{label}</span>
      <span className="font-mono text-lg font-semibold">{pct}%</span>
    </div>
  );
}

function QuoteCard({ quote, tint }) {
  const border = tint === "emerald" ? "border-emerald-200" : "border-red-200";
  const bg = tint === "emerald" ? "bg-emerald-50/60" : "bg-red-50/50";
  return (
    <blockquote className={`rounded-md border ${border} ${bg} p-3`}>
      <p className="text-[13px] text-stone-800 leading-snug">"{quote.text}"</p>
      <p className="mt-2 text-[10px] font-mono text-stone-500 uppercase tracking-wider">
        {quote.year_of_study} · {quote.course_name}
      </p>
    </blockquote>
  );
}

const FIELD_LABEL = {
  overall_satisfaction:  "NPS · likely to recommend",
  teaching_quality:      "Teaching quality",
  value_for_money:       "Value for money",
  campus_and_facilities: "Campus & facilities",
  student_support:       "Wellbeing support",
  career_prep:           "Careers support",
  social_life:           "Social life",
  accommodation:         "Accommodation",
  content_quality:       "Content quality",
  teaching_engagement:   "Teaching engagement",
  assessment_fairness:   "Assessment fairness",
  workload_manageable:   "Workload manageable",
  career_relevance:      "Career relevance",
};
